import { Box, Group, LoadingOverlay, Stack, TagsInput } from "@mantine/core";
import EmailAccountSelector from "../../../../../components/Selectors/EmailAccountSelector";
import { useMemo, useState } from "react";
import { sendEmailWithFiles, sendEmail } from "../../../../../api/WorkflowAPI";
import { notifications } from "@mantine/notifications";
import EmailAnswerEditor from "./EmailAnswerEditor";
import EmailSignatureSelector from "../../../../../components/Selectors/EmailSignatureSelector";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  useAccountsStore,
  useConfigStore,
} from "../../../../../stores/ConfigStore";
import { getCcAsArray } from "../../utils";

const AnswerSender = ({
  prospect,
  item,
  setAnswering,
  about,
  setProspectLoading,
  onAnswerSent,
  prog,
  modelSubject,
}) => {
  const lang = useLanguage((s) => s.language);
  const accounts = useAccountsStore((s) => s.email_accounts);
  const signs = useConfigStore((s) => s.email_signatures);
  const [sender, setSender] = useState(
    item?.reply_to?.integration_id
      ? { id: item?.reply_to?.integration_id }
      : item?.integration_id
        ? { id: item?.integration_id }
        : accounts.length === 1
          ? accounts[0]
          : null,
  );
  const [recipients, setRecipients] = useState([
    about?.email || "",
    ...(accounts
      ?.filter((x) => x.add_sender_as_recipient_in_replies)
      ?.map((item) => item?.email) || []),
  ]);

  const [ccRecipients, setCcRecipients] = useState(
    getCcAsArray(item?.message || ""),
  );

  const [sign, setSign] = useState(
    item?.reply_to?.signature_id
      ? { id: item?.reply_to?.signature_id }
      : signs.length === 1
        ? signs[0]
        : null,
  );
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const subject = useMemo(() => {
    let html = item?.reply || item?.message || "";
    const match = html.match(/Subject\s*:\s*(.*?)<br>/i);
    let result = match?.[1].trim() || "";

    return modelSubject || result ? `Re: ${modelSubject || result}` : "";
  }, [item?.message, item?.reply, modelSubject]);

  const handleInit = (editor) => {
    setInitialized(true);

    if (sign) {
      editor.execCommand(
        "mceInsertContent",
        false,
        "<br /><br /><br /><br /><br /><br /><br /><br /><br /> %signature%",
      );
    }
  };

  const handleSubmit = ({ html, text, subject, files }) => {
    let oldHtml = item?.reply || item?.message;

    let newHtml = html;

    let parsedHtml = item
      ? `
      ${newHtml}
      <br />

      <div style="
          border: none;
          border-top: solid #e1e1e1 1pt;
          padding: 3pt 0in 0in 0in;
        ">
      </div>

      ${oldHtml || ""}
    `
      : newHtml;

    setLoading(true);

    let formData = new FormData();
    let data = {
      html: parsedHtml,
      text,
      subject,
      email_account_id: sender?.id,
      recipients,
      cc_recipients: ccRecipients,
      email_signature_id: sign?.id,
      // contact_id_prm:
      //   item?.id || item?.event === "email_sent" ? undefined : prospect?.id,
      // reply_id: item?.history_type === "sent" ? undefined : item?.id,
      // programmation_contact_id:
      //   item?.history_type === "sent" || item?.event === "email_sent"
      //     ? prog?.programmation_contact_id
      //     : undefined,
    };
    if (item?.history_type !== "sent" && item?.id) {
      data.reply_id = item?.id;
    }
    if (prog?.programmation_contact_id) {
      data.programmation_contact_id = prog?.programmation_contact_id;
    } else if (item?.event !== "email_sent") {
      data.contact_id_prm = prospect?.id;
    }
    Object.keys(data).forEach((key) => {
      if (key === "recipients") {
        return recipients.forEach((value) => {
          formData.append("recipients[]", value); // Use "myArray[]" to indicate an array
        });
      }
      if (key === "cc_recipients") {
        return ccRecipients.forEach((value) => {
          formData.append("cc_recipients[]", value); // Use "myArray[]" to indicate an array
        });
      }
      if (data[key]) formData.append(key, data[key]);
    });

    files?.forEach((file) => {
      formData.append("attachments[]", file); // Use "myArray[]" to indicate an array
    });

    let main = files.length > 0 ? sendEmailWithFiles : sendEmail;
    main(files.length > 0 ? formData : data)
      .then((res) => {
        notifications.show({
          message: lang.workflows.nodes.events.email_sent,
          color: "green.8",
        });
        setAnswering(false);
        setProspectLoading(true);
        onAnswerSent?.();
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box pos={"relative"} h={"auto"} bg={"#fff"}>
      <LoadingOverlay visible={loading || !initialized} />

      {initialized && (
        <Group
          w={"100%"}
          wrap="nowrap"
          gap={"lg"}
          px={8}
          pl={4}
          pt={4}
          pos={"relative"}
          pb={16}
          style={{
            zIndex: 4,
            background: "#fff",
          }}
          align="start"
        >
          <Stack w={"100%"} gap={"xs"}>
            <TagsInput
              type="email"
              value={recipients}
              onChange={setRecipients}
              label={lang.components.email_preview.recipients}
              placeholder={"email@example.com"}
              w={"100%"}
              required
            />
            <TagsInput
              type="email"
              value={ccRecipients}
              onChange={setCcRecipients}
              label={"Cc"}
              placeholder={"email@example.com"}
              w={"100%"}
            />
          </Stack>
          <EmailAccountSelector
            label={lang.components.email_preview.sender}
            size={"sm"}
            required
            onChange={(s) => {
              setSender(s);

              if (s?.email_signatures_profile) {
                setSign(s.email_signatures_profile);
              }
            }}
            value={sender?.id}
            smtpOnly={"semi"}
          />
          <EmailSignatureSelector
            value={sign?.id}
            onChange={setSign}
            label={lang.emails_account.columns.signature}
            size={"sm"}
            w="100%"
            unclearable={!!sender?.email_signatures_profile}
          />
        </Group>
      )}

      <EmailAnswerEditor
        key={item?.id}
        id={item?.id}
        handleSubmit={handleSubmit}
        handleClose={() => setAnswering(false)}
        onInit={handleInit}
        loading={loading}
        disabled={!sender || recipients.length === 0}
        sign={sign?.id}
        defaultSubject={
          item?.reply_to?.subject ? `Re : ${item.reply_to.subject}` : subject
        }
      />
    </Box>
  );
};

export default AnswerSender;
